import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiMoreVertical,
  FiPlus,
} from "react-icons/fi";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { LuAlertCircle, LuCheckCircle, LuUsers } from "react-icons/lu";
import {
  AccessLevelName,
  accessLevelClassName,
  accessLevelDisplayName,
} from "./PayoutAccessControlData";
import PayoutDataTable from "./PayoutDataTable";
import PayoutDateRangeFilter from "./PayoutDateRangeFilter";
import PayoutDropdown from "./PayoutDropdown";
import PayoutFilterActions from "./PayoutFilterActions";
import PayoutFilterChips from "./PayoutFilterChips";
import PayoutSearchFilter from "./PayoutSearchFilter";
import PayoutTableCell from "./PayoutTableCell";
import {
  formatPayoutDateRangeLabel,
  parsePayoutTableDate,
  type PayoutDateRangeValue,
} from "./payoutDateUtils";
import { isPayoutApiMode, payoutApi } from "./api/payoutApiClient";
import { apiNumber, userFromApiRecord } from "./api/payoutApiAdapters";
import { usePayoutResource } from "./api/usePayoutResource";

const userStats = [
  {
    label: "Total Users",
    value: "128",
    helper: "+ 12.5% vs last month",
    helperClass: "text-[#16A34A]",
    icon: <LuUsers />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
  {
    label: "Active Users",
    value: "96",
    helper: "75.0% of total",
    helperClass: "text-[#505E8E]",
    icon: <LuCheckCircle />,
    tone: "bg-[#16A34A1A] text-[#16A34A]",
  },
  {
    label: "Inactive Users",
    value: "22",
    helper: "17.2% of total",
    helperClass: "text-[#505E8E]",
    icon: <LuAlertCircle />,
    tone: "bg-[#F59E0B1A] text-[#F97316]",
  },
  {
    label: "Suspended Users",
    value: "10",
    helper: "7.8% of total",
    helperClass: "text-[#505E8E]",
    icon: <LuAlertCircle />,
    tone: "bg-[#EF44441A] text-[#EF4444]",
  },
];

type UserRow = {
  initials: string;
  name: string;
  subtitle: string;
  email: string;
  mobile: string;
  role: string;
  accessLevel: AccessLevelName;
  payoutAccountIds: string[];
  status: string;
  lastLogin: string;
  avatar: string;
  id?: string;
  accountsAccess?: string;
};

const roleClassName: Record<string, string> = {
  "Finance Manager": accessLevelClassName["Finance Manager"],
  "Finance Executive": "bg-[#DBEAFE] text-[#0284C7]",
  Administrator: accessLevelClassName.Administrator,
  Observer: accessLevelClassName.Observer,
};

const getPayoutAccountLabel = (accountId: string) => {
  return accountId;
};

const getPayoutAccountDisplayName = (accountId: string) => {
  return accountId;
};

const getUserAccountAccessList = (user: UserRow) =>
  (user.accountsAccess
    ? user.accountsAccess.split(",").map((account) => account.trim()).filter(Boolean)
    : user.payoutAccountIds
  ).map(getPayoutAccountDisplayName);

const toApiDate = (date: Date) => date.toISOString().slice(0, 10);

const compactApiQuery = (
  query: Record<string, string | number | undefined>,
) =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ""),
  ) as Record<string, string | number>;

const userSearchFieldToApi = (field: string) => {
  switch (field) {
    case "Email":
      return "email";
    case "Mobile":
      return "mobile";
    case "Role":
      return "role";
    case "Access Level":
      return "access_level";
    case "Payout Account":
      return "funding_account";
    case "Status":
      return "status";
    case "User Name":
    default:
      return "name";
  }
};

const statusClassName: Record<string, string> = {
  Active: "bg-[#DCFCE7] text-[#16A34A]",
  Inactive: "bg-[#E5E7EB] text-[#505E8E]",
  Suspended: "bg-[#FEE2E2] text-[#EF4444]",
};

function AccountAccessList({ accounts }: { accounts: string[] }) {
  const visibleAccounts = accounts.slice(0, 3);
  const remainingCount = Math.max(0, accounts.length - visibleAccounts.length);
  const moreButtonRef = React.useRef<HTMLButtonElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const updateTooltipPosition = useCallback(() => {
    const rect = moreButtonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const tooltipWidth = 320;
    setTooltipPosition({
      top: rect.bottom + 8,
      left: Math.max(12, Math.min(rect.left, window.innerWidth - tooltipWidth - 12)),
    });
  }, []);

  useEffect(() => {
    if (!tooltipOpen) return undefined;
    updateTooltipPosition();
    window.addEventListener("resize", updateTooltipPosition);
    window.addEventListener("scroll", updateTooltipPosition, true);
    return () => {
      window.removeEventListener("resize", updateTooltipPosition);
      window.removeEventListener("scroll", updateTooltipPosition, true);
    };
  }, [tooltipOpen, updateTooltipPosition]);

  if (!accounts.length) {
    return <span className="font-semibold text-[#505E8E]">-</span>;
  }

  return (
    <div className="max-w-[240px] space-y-1 font-semibold">
      {visibleAccounts.map((account, index) => (
        <PayoutTableCell
          key={`${account}-${index}`}
          value={account}
          className="!min-h-[18px]"
          valueClassName="font-semibold text-[#1B163B]"
          maxWidthClassName="max-w-[220px]"
        />
      ))}
      {remainingCount > 0 && (
        <span
          className="inline-flex"
          onMouseEnter={() => {
            updateTooltipPosition();
            setTooltipOpen(true);
          }}
          onMouseLeave={() => setTooltipOpen(false)}
          onFocus={() => {
            updateTooltipPosition();
            setTooltipOpen(true);
          }}
          onBlur={() => setTooltipOpen(false)}
        >
          <button
            ref={moreButtonRef}
            type="button"
            className="text-xs font-semibold text-[#6687FF] outline-none"
            aria-label={`Show all ${accounts.length} account access entries`}
          >
            +{remainingCount} more
          </button>
          {tooltipOpen &&
            createPortal(
              <span
                className="pointer-events-none fixed w-[320px] rounded-[6px] border border-[#E0E5F2] bg-white p-3 text-left text-xs font-semibold leading-5 text-[#1B163B] shadow-[0_16px_36px_rgba(27,22,59,0.16)]"
                style={{
                  top: tooltipPosition.top,
                  left: tooltipPosition.left,
                  zIndex: 2147483647,
                }}
              >
                <span className="mb-2 block text-[11px] uppercase text-[#505E8E]">
                  Accounts Access
                </span>
                <span className="block max-h-[220px] space-y-1 overflow-y-auto">
                  {accounts.map((account, index) => (
                    <span key={`${account}-tooltip-${index}`} className="block break-all">
                      {account}
                    </span>
                  ))}
                </span>
              </span>,
              document.body,
            )}
        </span>
      )}
    </div>
  );
}

function SelectBox({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: string;
  options?: string[];
  onChange?: (value: string) => void;
  className?: string;
}) {
  return (
    <PayoutDropdown
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      className={className}
    />
  );
}

function PayoutUsers() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("User Name");
  const [roleFilter, setRoleFilter] = useState("All Roles");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [accountFilter, setAccountFilter] = useState("All Accounts");
  const [dateFilterApplied, setDateFilterApplied] = useState(false);
  const [appliedDateRange, setAppliedDateRange] = useState<PayoutDateRangeValue | null>(
    null,
  );
  const [pageSize, setPageSize] = useState("10 per page");
  const [currentPage, setCurrentPage] = useState(1);
  const [actionMenu, setActionMenu] = useState<{
    userKey: string;
    user: UserRow;
    anchor: HTMLButtonElement;
    top: number;
    left: number;
  } | null>(null);
  const [deletedUserEmails] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("payoutDeletedUserEmails") || "[]");
    } catch {
      return [];
    }
  });
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    role: "All Roles",
    status: "All Status",
    account: "All Accounts",
    searchField: "User Name",
  });
  const rowsPerPage = Number(pageSize.split(" ")[0]) || 10;
  const userApiQuery = useMemo(
    () =>
      compactApiQuery({
        limit: rowsPerPage,
        page: currentPage,
        q: appliedFilters.search.trim() || undefined,
        search_field: appliedFilters.search.trim()
          ? userSearchFieldToApi(appliedFilters.searchField)
          : undefined,
        role: appliedFilters.role !== "All Roles" ? appliedFilters.role : undefined,
        status:
          appliedFilters.status !== "All Status" ? appliedFilters.status : undefined,
        funding_account:
          appliedFilters.account !== "All Accounts" ? appliedFilters.account : undefined,
        last_login_from:
          dateFilterApplied && appliedDateRange
            ? toApiDate(appliedDateRange.startDate)
            : undefined,
        last_login_to:
          dateFilterApplied && appliedDateRange
            ? toApiDate(appliedDateRange.endDate)
            : undefined,
      }),
    [appliedFilters, appliedDateRange, currentPage, dateFilterApplied, rowsPerPage],
  );
  const usersResource = usePayoutResource<UserRow[]>(
    [] as UserRow[],
    (signal) =>
      payoutApi.users(isPayoutApiMode() ? userApiQuery : {}, signal).then((rows) =>
        rows.map((row) => userFromApiRecord(row)) as UserRow[],
      ),
    [userApiQuery],
  );
  const usersSummaryResource = usePayoutResource<Record<string, unknown> | null>(
    null,
    (signal) => payoutApi.usersSummary(signal),
  );
  const baseUsers: UserRow[] = usersResource.data;
  const displayedUserStats = useMemo(() => {
    const summary = usersSummaryResource.data || {};
    const total = apiNumber(summary, ["total_users", "totalUsers", "total", "count"], baseUsers.length);
    const active = apiNumber(
      summary,
      ["active_users", "activeUsers", "active"],
      baseUsers.filter((user) => user.status === "Active").length,
    );
    const inactive = apiNumber(
      summary,
      ["inactive_users", "inactiveUsers", "inactive"],
      baseUsers.filter((user) => user.status === "Inactive").length,
    );
    const suspended = apiNumber(
      summary,
      ["suspended_users", "suspendedUsers", "suspended"],
      baseUsers.filter((user) => user.status === "Suspended").length,
    );
    const percent = (value: number) =>
      total > 0 ? `${((value / total) * 100).toFixed(1)}% of total` : "0.0% of total";

    return userStats.map((stat) => {
      if (stat.label === "Total Users") {
        return { ...stat, value: String(total), helper: "Live API total" };
      }
      if (stat.label === "Active Users") {
        return { ...stat, value: String(active), helper: percent(active) };
      }
      if (stat.label === "Inactive Users") {
        return { ...stat, value: String(inactive), helper: percent(inactive) };
      }
      if (stat.label === "Suspended Users") {
        return { ...stat, value: String(suspended), helper: percent(suspended) };
      }
      return stat;
    });
  }, [baseUsers.length, usersSummaryResource.data, usersSummaryResource.source]);
  const expandedUsers = useMemo(
    () => baseUsers,
    [baseUsers],
  );
  const payoutAccountOptions = useMemo(
    () => [
      "All Accounts",
      ...Array.from(
        new Set(
          baseUsers.flatMap((user) =>
            user.accountsAccess
              ? user.accountsAccess.split(",").map((account) => account.trim()).filter(Boolean)
              : user.payoutAccountIds,
          ),
        ),
      ),
    ],
    [baseUsers],
  );
  const getUserRouteKey = useCallback((user: UserRow) => user.id || user.email, []);

  const filteredUsers = useMemo(() => {
    if (usersResource.source === "api") {
      return expandedUsers.filter(
        (user) => !deletedUserEmails.includes(user.email),
      );
    }

    const query = appliedFilters.search.trim().toLowerCase();
    const getUserSearchValue = (
      user: (typeof expandedUsers)[number],
      field: string,
    ) => {
      switch (field) {
        case "Email":
          return user.email;
        case "Mobile":
          return user.mobile;
        case "Role":
          return user.role;
        case "Access Level":
          return accessLevelDisplayName[user.accessLevel];
        case "Payout Account":
          return user.payoutAccountIds.map(getPayoutAccountLabel).join(" ");
        case "Status":
          return user.status;
        case "User Name":
        default:
          return user.name;
      }
    };
    return expandedUsers.filter((user) => {
      if (deletedUserEmails.includes(user.email)) return false;
      const matchesRole =
        appliedFilters.role === "All Roles" || user.role === appliedFilters.role;
      const matchesStatus =
        appliedFilters.status === "All Status" || user.status === appliedFilters.status;
      const matchesAccount =
        appliedFilters.account === "All Accounts" ||
        (user.accountsAccess || "")
          .split(",")
          .map((account) => account.trim())
          .includes(appliedFilters.account) ||
        user.payoutAccountIds.some(
          (accountId) => getPayoutAccountLabel(accountId) === appliedFilters.account,
        );
      const lastLoginDate = parsePayoutTableDate(user.lastLogin);
      const matchesDate =
        !dateFilterApplied ||
        !appliedDateRange ||
        user.lastLogin === "-" ||
        (lastLoginDate !== null &&
          lastLoginDate >= appliedDateRange.startDate &&
          lastLoginDate <= appliedDateRange.endDate);
      const matchesSearch =
        !query ||
        getUserSearchValue(user, appliedFilters.searchField)
          .toLowerCase()
          .includes(query);

      return matchesRole && matchesStatus && matchesAccount && matchesDate && matchesSearch;
    });
  }, [
    appliedDateRange,
    appliedFilters,
    dateFilterApplied,
    deletedUserEmails,
    expandedUsers,
    usersResource.source,
  ]);
  const apiUsersTotal = apiNumber(
    usersSummaryResource.data || {},
    ["total_users", "totalUsers", "total", "count"],
    filteredUsers.length,
  );
  const displayTotal =
    usersResource.source === "api" ? apiUsersTotal : filteredUsers.length;
  const pageCount = Math.max(
    1,
    Math.ceil(
      (usersResource.source === "api" ? apiUsersTotal : filteredUsers.length) /
        rowsPerPage,
    ),
  );
  const visibleUsers =
    usersResource.source === "api"
      ? filteredUsers
      : filteredUsers.slice(
          (currentPage - 1) * rowsPerPage,
          currentPage * rowsPerPage,
        );
  const resultStart = displayTotal ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const resultEnd =
    usersResource.source === "api"
      ? Math.min(currentPage * rowsPerPage, displayTotal)
      : Math.min(currentPage * rowsPerPage, filteredUsers.length);

  const getUserActionMenuPosition = useCallback((anchor: HTMLButtonElement) => {
    const rect = anchor.getBoundingClientRect();
    const menuWidth = 180;

    return {
      top: rect.bottom + 8,
      left: Math.max(12, rect.right - menuWidth),
      isVisible:
        rect.bottom > 0 &&
        rect.top < window.innerHeight &&
        rect.right > 0 &&
        rect.left < window.innerWidth,
    };
  }, []);

  const openUserActionMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    user: UserRow,
  ) => {
    event.stopPropagation();
    const anchor = event.currentTarget;
    const position = getUserActionMenuPosition(anchor);
    const userKey = getUserRouteKey(user);

    setActionMenu((current) =>
      current?.userKey === userKey
        ? null
        : {
            userKey,
            user,
            anchor,
            top: position.top,
            left: position.left,
          },
    );
  };

  const handleUserAction = (
    user: UserRow,
    action: "View Details" | "Edit" | "Delete" | "Change Password",
  ) => {
    setActionMenu(null);
    const userKey = getUserRouteKey(user);
    const encodedUserId = encodeURIComponent(userKey);
    const routeState = { state: { payoutUser: user } };
    if (action === "View Details") {
      navigate(`/payouts/users/view/${encodedUserId}`, routeState);
      return;
    }
    if (action === "Edit") {
      navigate(`/payouts/users/edit/${encodedUserId}`, routeState);
      return;
    }
    if (action === "Change Password") {
      navigate(`/payouts/users/change-password/${encodedUserId}`, routeState);
      return;
    }
    navigate(`/payouts/users/delete/${encodedUserId}`, routeState);
  };

  const userTableRows = visibleUsers.map((user) => [
    <div className="flex items-center gap-4">
      <span
        className={
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold " +
          user.avatar
        }
      >
        {user.initials}
      </span>
      <div className="min-w-0">
        <PayoutTableCell
          value={user.name}
          valueClassName="font-semibold text-[#1B163B]"
        />
        <p className="mt-1 max-w-[220px] truncate text-[#505E8E]" title={user.subtitle}>
          {user.subtitle}
        </p>
      </div>
    </div>,
    <span>
      <PayoutTableCell
        value={user.email}
        valueClassName="font-semibold text-[#1B163B]"
      />
      <span className="mt-1 block max-w-[220px] truncate text-[#505E8E]" title={user.mobile}>
        {user.mobile}
      </span>
    </span>,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
        roleClassName[user.role]
      }
    >
      {user.role}
    </span>,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
        accessLevelClassName[user.accessLevel]
      }
    >
      {accessLevelDisplayName[user.accessLevel]}
    </span>,
    <AccountAccessList accounts={getUserAccountAccessList(user)} />,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
        statusClassName[user.status]
      }
    >
      {user.status}
    </span>,
    <PayoutTableCell
      value={user.lastLogin}
      valueClassName="font-semibold text-[#1B163B]"
    />,
    <div className="flex w-full items-center justify-center">
      <button
        type="button"
        onClick={(event) => openUserActionMenu(event, user)}
        className="flex h-8 w-8 items-center justify-center rounded-[6px] text-[#1B163B] hover:bg-[#A9B2CF1A]"
        data-payout-user-action-trigger
        aria-label={`Open actions for ${user.name}`}
      >
        <FiMoreVertical className="text-lg" />
      </button>
    </div>,
  ]);

  useEffect(() => {
    setAppliedFilters({
      search: searchQuery,
      role: roleFilter,
      status: statusFilter,
      account: accountFilter,
      searchField,
    });
    setCurrentPage(1);
  }, [searchQuery, searchField, roleFilter, statusFilter, accountFilter]);

  const handleDateRangeApply = (range: PayoutDateRangeValue) => {
    setAppliedDateRange(range);
    setDateFilterApplied(true);
    setCurrentPage(1);
  };

  const clearDateFilter = () => {
    setAppliedDateRange(null);
    setDateFilterApplied(false);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSearchField("User Name");
    setRoleFilter("All Roles");
    setStatusFilter("All Status");
    setAccountFilter("All Accounts");
    clearDateFilter();
  };

  const actionMenuAnchor = actionMenu?.anchor;

  useEffect(() => {
    if (!actionMenuAnchor) return;

    const updateActionMenuPosition = () => {
      setActionMenu((current) => {
        if (!current) return null;
        const position = getUserActionMenuPosition(current.anchor);

        if (!position.isVisible) return null;

        return {
          ...current,
          top: position.top,
          left: position.left,
        };
      });
    };

    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      if (
        target?.closest("[data-payout-user-action-menu]") ||
        target?.closest("[data-payout-user-action-trigger]")
      ) {
        return;
      }

      setActionMenu(null);
    };

    window.addEventListener("scroll", updateActionMenuPosition, true);
    window.addEventListener("resize", updateActionMenuPosition);
    document.addEventListener("mousedown", closeOnOutsideClick);

    return () => {
      window.removeEventListener("scroll", updateActionMenuPosition, true);
      window.removeEventListener("resize", updateActionMenuPosition);
      document.removeEventListener("mousedown", closeOnOutsideClick);
    };
  }, [actionMenuAnchor, getUserActionMenuPosition]);

  const exportUsers = () => {
    const headers = ["User", "Email", "Mobile", "Role", "Access Level", "Accounts Access", "Status", "Last Login"];
    const csvRows = filteredUsers.map((user) =>
      [
        user.name,
        user.email,
        user.mobile,
        user.role,
        accessLevelDisplayName[user.accessLevel],
        user.payoutAccountIds.map(getPayoutAccountLabel).join("; "),
        user.status,
        user.lastLogin,
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "payout-users.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Users
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Manage and view all users in your organization
          </p>
        </div>
        <Link
          to="/payouts/users/add"
          className="flex h-10 items-center gap-3 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white"
        >
          <FiPlus className="text-lg" />
          Add User
          <FiChevronDown />
        </Link>
      </div>

      <div className="mb-8 grid gap-5 lg:grid-cols-4">
        {displayedUserStats.map((stat) => (
          <div
            key={stat.label}
            className="flex min-h-[118px] items-center gap-5 rounded-[8px] border border-[#E0E5F2] bg-white px-6 py-5 new-shadow"
          >
            <div
              className={
                "payout-kpi-icon flex h-11 w-11 shrink-0 items-center justify-center text-xl " +
                stat.tone
              }
            >
              {stat.icon}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#505E8E]">
                {stat.label}
              </p>
              <p className="mt-2 text-[22px] font-semibold leading-7 text-[#1B163B]">
                {stat.value}
              </p>
              <p className={"mt-4 text-xs font-semibold " + stat.helperClass}>
                {stat.helper}
              </p>
            </div>
          </div>
        ))}
      </div>

      <section className="payout-filter-card mb-5 rounded-[8px] border border-[#E0E5F2] bg-white px-5 py-5 new-shadow">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[280px_190px_190px_minmax(220px,1fr)]">
          <PayoutDateRangeFilter
            label="Date"
            className="w-full min-w-0"
            displayText={
              dateFilterApplied
                ? formatPayoutDateRangeLabel(appliedDateRange)
                : "Date (all)"
            }
            displayTextMode="override"
            onChange={handleDateRangeApply}
          />
          <SelectBox
            label="Role"
            value={roleFilter}
            options={["All Roles", "Finance Manager", "Finance Executive", "Administrator", "Observer"]}
            onChange={setRoleFilter}
            className="w-full min-w-0"
          />
          <SelectBox
            label="Status"
            value={statusFilter}
            options={["All Status", "Active", "Inactive", "Suspended"]}
            onChange={setStatusFilter}
            className="w-full min-w-0"
          />
          <SelectBox
            label="Account"
            value={accountFilter}
            options={payoutAccountOptions}
            onChange={setAccountFilter}
            className="w-full min-w-0"
          />
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
          <PayoutSearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            filterBy={searchField}
            onFilterByChange={setSearchField}
            filterOptions={[
              "User Name",
              "Email",
              "Mobile",
              "Role",
              "Access Level",
              "Payout Account",
              "Status",
            ]}
            placeholder={`Search by ${searchField}`}
            className="w-full min-w-0"
          />
          <PayoutFilterActions
            onClear={clearFilters}
            onExport={exportUsers}
            className="self-end"
          />
        </div>
        <div className="mt-4">
          <PayoutFilterChips
            onClearAll={clearFilters}
            chips={[
              {
                label: "Role",
                value: appliedFilters.role,
                onRemove: () => setRoleFilter("All Roles"),
              },
              {
                label: "Status",
                value: appliedFilters.status,
                onRemove: () => setStatusFilter("All Status"),
              },
              {
                label: "Account",
                value: appliedFilters.account,
                onRemove: () => setAccountFilter("All Accounts"),
              },
              {
                label: "Search",
                value: appliedFilters.search,
                onRemove: () => setSearchQuery(""),
              },
              ...(dateFilterApplied && appliedDateRange
                ? [
                    {
                      label: "Last login",
                      value: formatPayoutDateRangeLabel(appliedDateRange),
                      onRemove: clearDateFilter,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </section>

      <PayoutDataTable
        columns={[
          "User",
          "Email / Mobile",
          "Role",
          "Access Level",
          "Accounts Access",
          "Status",
          "Last Login",
          "Actions",
        ]}
        rows={userTableRows}
        minWidth="min-w-[1060px]"
        footer={
          <div className="flex min-h-[64px] w-full items-center justify-between gap-4 bg-white px-5 py-4 text-xs font-medium text-[#505E8E]">
            <span className="min-w-max">
              Showing {resultStart} to {resultEnd} of {displayTotal} results
            </span>
            <div className="ml-auto flex min-w-max items-center gap-4">
              <PayoutDropdown
                value={pageSize}
                options={["10 per page", "25 per page", "50 per page"]}
                onChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                className="min-w-[120px]"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowLeft className="text-xs" />
                </button>
                {[1, 2, 3].filter((page) => page <= pageCount).map((page) => (
                  <button
                    type="button"
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={
                      "h-9 w-9 rounded-md " +
                      (currentPage === page
                        ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                        : "border border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {page}
                  </button>
                ))}
                {pageCount > 3 && (
                  <>
                    <span className="px-2 text-[#505E8E]">...</span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(pageCount)}
                      className={
                        "h-9 w-9 rounded-md " +
                        (currentPage === pageCount
                          ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                          : "border border-[#E0E5F2] text-[#1B163B]")
                      }
                    >
                      {pageCount}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={currentPage === pageCount}
                  onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        }
      />

      <section className="hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1060px] table-auto">
            <thead className="bg-[#A9B2CF1A] text-center">
              <tr className="text-left text-[11px] font-semibold text-[#505E8E]">
                <th className="px-5 py-4 text-center">User</th>
                <th className="px-5 py-4 text-center">Email / Mobile</th>
                <th className="px-5 py-4 text-center">Role</th>
                <th className="px-5 py-4 text-center">Access Level</th>
                <th className="px-5 py-4 text-center">Accounts Access</th>
                <th className="px-5 py-4 text-center">Status</th>
                <th className="px-5 py-4 text-center">Last Login</th>
                <th className="px-5 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((user) => (
                <tr
                  key={user.email}
                  className="border-t border-[#E0E5F2] text-xs font-medium text-[#1B163B] hover:bg-[#A9B2CF1A]"
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-4">
                      <span
                        className={
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold " +
                          user.avatar
                        }
                      >
                        {user.initials}
                      </span>
                      <div>
                        <p className="font-semibold">{user.name}</p>
                        <p className="mt-1 text-[#505E8E]">{user.subtitle}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-semibold">{user.email}</p>
                    <p className="mt-1 text-[#505E8E]">{user.mobile}</p>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={
                        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        roleClassName[user.role]
                      }
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={
                        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        accessLevelClassName[user.accessLevel]
                      }
                    >
                      {accessLevelDisplayName[user.accessLevel]}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <AccountAccessList accounts={getUserAccountAccessList(user)} />
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={
                        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        statusClassName[user.status]
                      }
                    >
                      {user.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 font-semibold">{user.lastLogin}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-center">
                      <button
                        type="button"
                        onClick={(event) => openUserActionMenu(event, user)}
                        className="flex h-8 w-8 items-center justify-center rounded-[6px] text-[#1B163B] hover:bg-[#A9B2CF1A]"
                        data-payout-user-action-trigger
                        aria-label={`Open actions for ${user.name}`}
                      >
                        <FiMoreVertical className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex min-h-[64px] w-full items-center justify-between gap-4 border-t border-[#E0E5F2] px-5 py-4 text-xs font-medium text-[#505E8E]">
          <span className="min-w-max">
            Showing {resultStart} to {resultEnd} of {filteredUsers.length} results
          </span>
          <div className="ml-auto flex min-w-max items-center gap-4">
            <PayoutDropdown
              value={pageSize}
              options={["10 per page", "25 per page", "50 per page"]}
              onChange={(value) => {
                setPageSize(value);
                setCurrentPage(1);
              }}
              className="min-w-[120px]"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowLeft className="text-xs" />
              </button>
              {[1, 2, 3].filter((page) => page <= pageCount).map((page) => (
                <button
                  type="button"
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={
                    "h-9 w-9 rounded-md " +
                    (currentPage === page
                      ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                      : "border border-[#E0E5F2] text-[#1B163B]")
                  }
                >
                  {page}
                </button>
              ))}
              {pageCount > 3 && (
                <>
                  <span className="px-2 text-[#505E8E]">...</span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(pageCount)}
                    className={
                      "h-9 w-9 rounded-md " +
                      (currentPage === pageCount
                        ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                        : "border border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {pageCount}
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={currentPage === pageCount}
                onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowRight className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </section>
      {actionMenu && typeof document !== "undefined" ? createPortal(
        <div
          className="fixed z-[80] w-[180px] rounded-[8px] border border-[#E0E5F2] bg-white py-2 new-shadow"
          style={{ top: actionMenu.top, left: actionMenu.left }}
          data-payout-user-action-menu
          onMouseDown={(event) => event.stopPropagation()}
        >
          {(["View Details", "Edit", "Change Password", "Delete"] as const).map((action) => (
            <button
              key={action}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleUserAction(actionMenu.user, action)}
              className={
                "block w-full px-4 py-2 text-left text-sm font-semibold hover:bg-[#A9B2CF1A] " +
                (action === "Delete" ? "text-[#991B1B]" : "text-[#1B163B]")
              }
            >
              {action}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

export default PayoutUsers;
